import EventContainer from "./eventContainer";
import EventUtil from '../event/eventUtil'

/**
 * --- README ---
 * 
 * There is some key-logic used to efficiently store and access selected days / events which must be understood to add functionality to this class.
 * 
 * We can generate three kinds of key: yyyy-mm-dd, yyyy-mm-dd-userId & yyyy-mm-dd-userId-eventId.
 * In general, each is named as 'dKey', 'sKey' & 'eKey' when it appears in code. 'dKey' MUST BE generated from the EventContainer.getKey() method
 * 
 * To generate a key, call _assocKey(). The type of key is inferred from the params passed.
 * We can also 'promote' and 'demote' a key to move to a greater or lesser specificity, depending on the dictionary requirement.
 * See _assocKey(), _promoteKey(), _demoteKey() below.
 * 
 * We can neatly access all sub-keys of a lesser specificity with filter(subKey => subKey.startsWith(key)).
 * There is no information loss, so if needed we can parse the key with split('-') or by scalping the last value to access the IDs embedded.
 * 
 * For ease-of-use, this isn't usually necessary; Often the dictionary value of the key is set to whatever data is required.
 * 
 * --- END ---
 */


/**
 * Contains logic required to modify student-event associations on a timetable.
 * 
 * The EventEditor exposes an inner EventContainer and the methods called by the AdminTimetable updateDays() method.
 * As such, we can use this class in place of the EventContainer when editing a timetable, modifying the displayed associations in accord with edits made.
 */
class EventEditor {
    #container

    #editStack = []
    #addUsers = {}              // Uses an 'eKey':  yyyy-mm-dd-userId-eventId
    #removeUsers = {}           // Uses an 'eKey':  yyyy-mm-dd-userId-eventId

    #eventsSelected = {}        // Uses an 'eKey':  yyyy-mm-dd-userId-eventId

    #daysSelected = {}          // Uses a 'dKey':   yyyy-mm-dd
    #studentDaysSelected = {}   // Uses an 'sKey':  yyyy-mm-dd-userId
    #countSelected = 0
    #availableEvents = []

    /**
     * @param {EventContainer} c 
     */
    constructor(c = new EventContainer) {
        this.#container = c
    }

    dumpEdit() {
        return {
            add: Object.values(this.#addUsers).map(a => ({ event_id: a.eventId, user_id: a.studentId })),
            remove: Object.values(this.#removeUsers).map(r => ({ event_id: r.eventId, user_id: r.studentId })),
        }
    }


    /****** BEGIN - These methods are so that the eventEditor can be used as its inner eventContainer (in the AdminTimetable.vue updateDays() methods) ******/

    /**
     * Returns the events according to the inner container, filtered by the current edit state.
     * 
     * @param {integer|string} year E.g. 1999
     * @param {integer|string} month 0-11
     * @param {integer|string} date 1-31
     * @param {integer|string|null} userId
     */
    getStudentEvents(year, month, date, userId=null) {
        const dKey = this._assocKey(year, month, date)
        let sortedEvents

        if (!userId) {
            if (this.#container.eventsByStudent[dKey]) {
                sortedEvents = { ...this.#container.eventsByStudent[dKey] }

                Object.keys(this.#container.eventsByStudent[dKey]).forEach(studentId => {
                    sortedEvents[studentId] = sortedEvents[studentId].filter(e => !this.#removeUsers[this._promoteKey(dKey, studentId, e.id)])
                })
            }
            else sortedEvents = {}

            let add
            Object.keys(this.#addUsers).filter(k => k.startsWith(dKey)).forEach(eKey => {
                add = this.#addUsers[eKey]
                if (!sortedEvents[add.studentId]) sortedEvents[add.studentId] = []
                sortedEvents[add.studentId].push(this.#container.eventsById[add.eventId])
            })

            return sortedEvents
        }

        sortedEvents = this.#container.eventsByStudent[dKey][userId] 
        ? this.#container.eventsByStudent[dKey][userId].filter(e => !this.#removeUsers[this._promoteKey(dKey, userId, e.id)])
        : []

        Object.keys(this.#addUsers).filter(k => k.startsWith(this._promoteKey(dKey, userId))).forEach(eKey => {
            sortedEvents.push(this.#container.eventsById[this.#addUsers[eKey].eventId])
        })

        return sortedEvents
    }

    getYearEvents(year, month, date) {
        return this.#container.getYearEvents(year, month, date);
    }    

    getGroupEvents(year, month, date, groupId=null) {
        return groupId ? [] : {}
    }

    isEmpty() {
        return false
    }

    /****** END ******/

    /**
     * Selects or deselects a particular event-student association.
     * 
     * @param {integer|string} year E.g. 1999
     * @param {integer|string} month 0-11
     * @param {integer|string} day 1-31
     * @param {integer|string} studentId
     * @param {integer|string} eventId
     */
    toggleEvent(year, month, day, studentId, eventId) {
        const key = this._assocKey(year, month, day, studentId, eventId)

        if (this.#eventsSelected[key]) delete this.#eventsSelected[key]

        else this.#eventsSelected[key] = { studentId, eventId }
    }

    /**
     * Tests selected status for a particular event-student association.
     * 
     * @param {integer|string} year E.g. 1999
     * @param {integer|string} month 0-11
     * @param {integer|string} day 1-31
     * @param {integer|string} studentId
     * @param {integer|string} eventId
     */
    eventIsSelected(year, month, day, studentId, eventId) {
        return !!this.#eventsSelected[this._assocKey(year, month, day, studentId, eventId)]
    }

    removeSelected() {
        this._snapshot()

        Object.keys(this.#eventsSelected).forEach(eKey => {
            if (this.#addUsers[eKey]) {
                delete this.#addUsers[eKey]
                return
            }

            const ids = eKey.split('-').slice(3)
            this.#removeUsers[eKey] = { eventId: ids[1], studentId: ids[0] }
        })

        this.#eventsSelected = {}
    }

    countSelected() {
        return Object.values(this.#eventsSelected).length
    }

    countStudents() {
        return Object.keys(Object.values(this.#eventsSelected).map(val => val.studentId).reduce((prev, curr) => {
            prev[curr] = true
            return prev
        }, {})).length
    }

    /**
     * Selects a date for a particular student, stores its events and recalculates the available events for all currently selected days.
     * 
     * Available events are the shared events (as determined by EventUtil.getMatchIntersection) of all days selected.
     * 
     * @param {integer|string} year E.g. 1999
     * @param {integer|string} month 0-11
     * @param {integer|string} day 1-31
     * @param {integer|string} studentId
     * @param {Array} events All events on this day
     * 
     * @returns {Array} The new set of available events
     */
    selectDay(year, month, day, studentId, events) {
        const dKey = this._assocKey(year, month, day)

        if (this.#daysSelected[dKey]) {
            this.#studentDaysSelected[this._promoteKey(dKey, studentId)] = true
            return this.#availableEvents
        }

        this.#daysSelected[dKey] = events
        this.#studentDaysSelected[this._promoteKey(dKey, studentId)] = true

        if (this.#countSelected) this.#availableEvents = EventUtil.getMatchIntersection(this.#availableEvents, events)
        else this.#availableEvents = events

        this.#countSelected++
        return this.#availableEvents
    }

    /**
     * Removes a particular selected day for the given student. 
     * 
     * If no student selections remain, the day itself is unselected and the available events are recalculated.
     * 
     * @param {integer|string} year E.g. 1999
     * @param {integer|string} month 0-11
     * @param {integer|string} day 1-31
     * @param {integer|string} studentId

     * @returns {Array} The new set of available events 
     */
    unselectDay(year, month, day, studentId) {
        delete this.#studentDaysSelected[this._assocKey(year, month, day, studentId)]

        const dKey = this._assocKey(year, month, day)
        if (Object.keys(this.#studentDaysSelected).filter(k => k.startsWith(dKey)).length) return this.#availableEvents

        delete this.#daysSelected[dKey]

        let newAvailable = []
        let firstLoop = true

        Object.values(this.#daysSelected).forEach(events => {
            if (firstLoop) {
                newAvailable = events
                firstLoop = false
                return
            }
            newAvailable = EventUtil.getMatchIntersection(newAvailable, events)
        })

        this.#availableEvents = newAvailable

        this.#countSelected--
        return this.#availableEvents
    }

    addToSelected(event) {
        this._snapshot()

        const eventIds = []

        outer: for (const events of Object.values(this.#daysSelected)) {
            for (const e of events) {
                if (!EventUtil.eventsMatch(event, e)) continue

                eventIds.push(e.id)
                continue outer
            }
        }

        let foundEvent
        let eKey
        let studentId

        Object.keys(this.#daysSelected).forEach(dKey => {
            foundEvent = this.#daysSelected[dKey].find(e => EventUtil.eventsMatch(event, e))

            Object.keys(this.#studentDaysSelected).filter(k => k.startsWith(dKey)).forEach(sKey => {
                eKey = this._promoteKey(sKey, foundEvent.id)
                if (this.#removeUsers[eKey]) {
                    delete this.#removeUsers[eKey]
                    return
                }

                studentId = sKey.slice(dKey.length+1)
                if (this.#container.checkExists(k => k === dKey, i => i === studentId, e => e.id === foundEvent.id)) return
                this.#addUsers[eKey] = { studentId, eventId: foundEvent.id }
            })
        })

        this.resetSelected()
    }

    dayIsSelected(year, month, day, studentId) {
        return this.#studentDaysSelected[this._assocKey(year, month, day, studentId)]
    }

    selectedDayCount() {
        return this.#countSelected
    }

    resetSelected() {
        this.#daysSelected = {}
        this.#studentDaysSelected = {}
        this.#countSelected = 0
        this.#availableEvents = []
    }

    undo() {
        if (!this.#editStack.length) return

        const latest = this.#editStack.pop()
        this.#addUsers = latest.addUsers
        this.#removeUsers = latest.removeUsers
    }

    canUndo() {
        return !!this.#editStack.length
    }

    _snapshot() {
        this.#editStack.push(JSON.parse(JSON.stringify({ addUsers: { ...this.#addUsers }, removeUsers: { ...this.#removeUsers }})))
    }

    _assocKey(year, month, day, studentId=null, eventId=null) {
        return `${EventContainer.getKey(year, month, day)}${studentId ? '-'+studentId : ''}${eventId ? '-'+eventId : ''}`
    }

    _promoteKey(key, id1, id2=null) {
        return `${key}-${id1}${id2 ? '-'+id2 : ''}`
    }

    _demoteKey(key) {
        return key.slice(0, key.lastIndexOf('-'))
    }
}

export default EventEditor